import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, inject, OnChanges } from '@angular/core';
import { forEach, isEmpty, max, toInteger } from 'lodash';
import { INews2, ReleaseNotesService } from './releasenotes.service';
import { LayoutHeaderFlyoutMenuComponent } from '../layout-header-flyout-menu/layout-header-flyout-menu.component';
import { StatuspalService } from '../../../helper/services/statuspal.service';
import { UiTranslateService } from '../../../helper/lang/ui-translate.service';

export interface IMaintenance {
  message: string;
  name: string;
  scheduled_at: string;
  scheduled_today: boolean;
  status: number;
  statusClass: string;
  time_left: string;
}

@Component({
    selector: 'ig-layout-header-notification-center',
    templateUrl: './layout-header-notification-center.component.html',
    styleUrls: ['./layout-header-notification-center.component.scss'],
    standalone: false
})
export class LayoutHeaderNotificationCenterComponent implements OnInit, OnChanges {
  /**
   * whether to fetch and show releasenotes (may be disabled for special partners)
   */
  @Input() showReleaseNotes = true;
  /**
   * whether to fetch and show status (may be disabled for special partners)
   */
  @Input() showStatus = true;
  /**
   * URL to release notes page ("read more" link)
   */
  @Input() releaseNotesUrl?: string;
  /**
   * statuspal API
   */
  @Input() statusApi: string = '';
  /**
   * URL to the status page
   */
  @Input() statusPage?: string;
  /**
   * unix timestamp of the latest news message the user read (controls the unread-badge)
   */
  @Input() lastReadTs: number = 0;
  /**
   * additional maintenances (e.g. for the current partner)
   */
  @Input() maintenances: IMaintenance[] = [];
  /**
   * the reference to the flyout container
   */
  @Input() flyoutContainer?: LayoutHeaderFlyoutMenuComponent;

  @Output() onNewsRead: EventEmitter<number> = new EventEmitter();

  private myIncidents: any[] = [];
  news: INews2[] = [];

  overalStatus: any;

  unreadReleaseNotes: number[] = [];
  isOpenTimeout: any;

  usageLimit: any[] = [];
  usageLimitWarnings: any[] = [];
  limitsExpanded = true;
  limitsExpandedAnimate = true;
  limitsOverviewExpanded = false;
  limitsOverviewExpandedAnimate = false;


  private readonly statuspalService = inject(StatuspalService);
  private readonly releaseNotesService = inject(ReleaseNotesService);
  private readonly translate = inject(UiTranslateService);



  ngOnChanges(changes: SimpleChanges) {
    if (changes['lastReadTs']) {
      this.handleUnread();
    }

    // fetch news
    if (changes['showReleaseNotes'] && !!this.showReleaseNotes) {
      this.fetchNews();
    }

    if (changes['statusApi'] && this.statusApi && this.showStatus) {
      this.fetchStatus();
    }

    if (changes['showStatus'] && this.statusApi && this.showStatus) {
      this.fetchStatus();
    }

    if (changes['maintenances'] && this.maintenances && this.statusApi && this.showStatus) {
      this.fetchStatus();
    }
  }

  private hideShow() {
    if (isEmpty(this.statusApi) && !this.showReleaseNotes) {
      this.flyoutContainer?.hideIcon();
    } else {
      this.flyoutContainer?.showIcon();
    }
  }

  private fetchStatus() {
    this.hideShow();

    // fetch incidents
    this.myIncidents = [];

    this.statuspalService.getImportant(this.statusApi).subscribe((_incidents: any[]) => {
      this.flyoutContainer?.removeBadgeMessage('incident');
      forEach(_incidents, (_incident: any) => {
        if (_incident.scheduled_today) {
          this.flyoutContainer?.addBadgeMessage('incident', _incident.status != '3' ? 2 : 1, _incident.id);
        }
      });

      this.myIncidents = _incidents;
    });

    // fetch platform status
    this.statuspalService.getOveralStatus(this.statusApi).subscribe((_overalStatus: any) => {
      this.flyoutContainer?.removeBadgeMessage('status');
      this.overalStatus = _overalStatus;
      if (_overalStatus.online && _overalStatus.issues) {
        this.flyoutContainer?.addBadgeMessage('status', 1);
      } else if (!_overalStatus.online) {
        this.flyoutContainer?.addBadgeMessage('status', 2);
      }
    });
  }

  private fetchNews() {
    this.hideShow();
    // fetch news
    this.releaseNotesService.fetch().subscribe(_publicNews => {
      this.news = _publicNews;

      this.handleUnread();
    });
  }



  ngOnInit() {
    if (this.showReleaseNotes) {
      this.fetchNews();
    }

    if (this.statusApi && this.showStatus) {
      this.fetchStatus();
    }

    // fetch the status again when language changes
    this.translate.onLangChange.subscribe(_lang => {
      if (this.statusApi && this.showStatus) {
        this.fetchStatus();
      }
    });

    this.flyoutContainer?.onOpen.subscribe(() => {
      if (this.isOpenTimeout) {
        window.clearTimeout(this.isOpenTimeout);
      }
      this.isOpenTimeout = window.setTimeout(() => {
        this.isOpenTimeout = null;
        // mark releases as read after 2secs
        this.flyoutContainer?.removeBadgeMessage('release_note');

        // save timestamp to uistore
        if (this.unreadReleaseNotes.length > 0 || this.lastReadTs === 0) {
          this.onNewsRead.emit(toInteger(max(this.unreadReleaseNotes)) + 1);
        }
      }, 2000);
    });

    this.flyoutContainer?.onClose.subscribe(() => {
      if (this.isOpenTimeout) {
        window.clearTimeout(this.isOpenTimeout);
      }
    });
  }

  handleUnread() {
    this.unreadReleaseNotes = [];
    if (this.lastReadTs > 0) {
      forEach(this.news, newsEntry => {
        if (newsEntry.attributes?.['time'] && newsEntry.attributes['time'] as number > this.lastReadTs) {
          this.unreadReleaseNotes.push(newsEntry.attributes['time']);
        }
      });



      if (this.unreadReleaseNotes.length) {
        forEach(this.unreadReleaseNotes, (unread: number) => {
          this.flyoutContainer?.addBadgeMessage('release_note', 0, String(unread));
        });
      }
    }
  }

  get incidents() {
    let incidents = this.myIncidents || [];

    if (this.maintenances && this.maintenances.length > 0) {
      incidents = [...this.myIncidents, ...this.maintenances];

      this.flyoutContainer?.addBadgeMessage('maintenance', 1);
    }

    return incidents;
  }
}
